import { Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useMerchant, isOwner } from "@/lib/useMerchant";

/**
 * RequireOwner.jsx
 * ------------------------------------------------------------------
 * Route guard for owner-only pages: Team, Billing, Shipping & Discounts,
 * Profile. The API already enforces this server-side (403s on every
 * write, and read-only endpoints like GET /profile return a trimmed
 * member-safe subset) — this guard is purely UX. Without it, a team
 * member who types /billing or /team directly doesn't get a 403 page;
 * they get a broken/half-rendered page (fields stuck loading, empty
 * states, spinners that never resolve) because the API calls those pages
 * make either fail or come back with less data than the page expects.
 * This sends them straight to /dashboard instead.
 *
 * NOTE: makes its own GET /api/me call via useMerchant() to check role
 * before rendering children — a second /me request on top of whatever
 * the wrapped page fetches internally (Billing.jsx also calls
 * useMerchant(); Team.jsx fetches /me directly). Cheap response, and
 * only applies on these 4 routes, so not worth optimizing away right
 * now. If it ever matters, pass `me` down via route context instead of
 * every page re-fetching its own copy.
 * ------------------------------------------------------------------
 */
export default function RequireOwner({ children }) {
  const { me, loading } = useMerchant();

  if (loading) {
    return (
      <section className="grid min-h-screen place-items-center bg-cream">
        <Loader2 className="h-6 w-6 animate-spin text-ink/40" />
      </section>
    );
  }

  if (!isOwner(me)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}
